import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import axios from 'axios';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch only PUBLISHED posts from the public Laravel endpoint
    axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/posts`)
      .then(response => {
        setPosts(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching posts:", error);
        setLoading(false);
      });
  }, []);

  // Helper function to safely extract a plain-text excerpt from the saved HTML
  const extractExcerpt = (htmlString, maxLength = 120) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const text = doc.body.textContent || "";
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-brand-light">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-heading font-black text-brand-dark mb-4"
          >
            Signage & Branding <span className="text-brand-cyan">Insights</span>
          </motion.h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-brand-blue to-brand-cyan mx-auto rounded-full"></div>
          <p className="mt-6 text-gray-600 max-w-2xl mx-auto text-lg">
            Expert tips, industry trends, and guides to help your business stand out.
          </p>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-500 py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
            <p className="text-lg">No articles published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow flex flex-col h-full">
                
                {/* Cover Image (Fetched from Laravel Storage) */}
                <div className="h-56 overflow-hidden bg-gray-100 relative">
                  <img 
                    src={`${process.env.REACT_APP_BASE_URL}/backend/api/image/${post.image_path}`} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                </div>
                
                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-bold mb-3 uppercase tracking-wider">
                    <Calendar size={14} />
                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                  
                  <h3 className="font-bold text-brand-dark text-xl mb-3 leading-tight group-hover:text-brand-cyan transition-colors">
                    {post.title}
                  </h3>
                  
                  {/* Safely inject the excerpt */}
                  <p className="text-sm text-gray-500 mb-6 flex-grow leading-relaxed">
                    {extractExcerpt(post.content)}
                  </p>
                  
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="mt-auto flex items-center gap-2 text-sm font-black uppercase tracking-widest text-brand-cyan hover:text-brand-dark transition-colors"
                  >
                    Read Article <ArrowRight size={16} />
                  </Link>
                </div>

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default BlogPage;